import React, { useEffect, useRef, useState } from 'react';
import WelcomeImage from './PopImg/welcome-popup.jpg';
import './discountPopup.css';

const CAMPAIGN = 'welcome-15-2026';

// Closing the popup does not hide it (it shows again on the next reload).
// Once a visitor has their code it stays hidden for this many days.
const SIGNED_UP_DAYS = 365;
const HIDE_COOKIE = 'brave_welcome_popup_hidden';
const CODE_KEY = 'brave_welcome_promo';

// TESTING: set to false before going live so a visitor who got a code stops seeing the popup
const IGNORE_COOKIE_FOR_TESTING = false;

// Hidden only for visitors who already generated a code (cookie, or the saved code in this browser)
const hasCode = () => {
  try {
    if (window.localStorage.getItem(CODE_KEY)) return true;
  } catch (error) {
    // storage blocked: the cookie below still works
  }
  return document.cookie.split('; ').some((item) => item.startsWith(`${HIDE_COOKIE}=`));
};

const isHidden = () => !IGNORE_COOKIE_FOR_TESTING && hasCode();

const hideFor = (days) => {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${HIDE_COOKIE}=1; expires=${expires}; path=/; SameSite=Lax`;
};

const COUNTRIES = [
  { iso: 'BH', name: 'Bahrain', code: '+973' }, { iso: 'SA', name: 'Saudi Arabia', code: '+966' },
  { iso: 'AE', name: 'United Arab Emirates', code: '+971' }, { iso: 'KW', name: 'Kuwait', code: '+965' },
  { iso: 'QA', name: 'Qatar', code: '+974' }, { iso: 'OM', name: 'Oman', code: '+968' },
  { iso: 'JO', name: 'Jordan', code: '+962' }, { iso: 'LB', name: 'Lebanon', code: '+961' },
  { iso: 'EG', name: 'Egypt', code: '+20' }, { iso: 'IQ', name: 'Iraq', code: '+964' },
  { iso: 'TR', name: 'Turkey', code: '+90' }, { iso: 'IN', name: 'India', code: '+91' },
  { iso: 'PK', name: 'Pakistan', code: '+92' }, { iso: 'BD', name: 'Bangladesh', code: '+880' },
  { iso: 'LK', name: 'Sri Lanka', code: '+94' }, { iso: 'PH', name: 'Philippines', code: '+63' },
  { iso: 'GB', name: 'United Kingdom', code: '+44' }, { iso: 'IE', name: 'Ireland', code: '+353' },
  { iso: 'FR', name: 'France', code: '+33' }, { iso: 'DE', name: 'Germany', code: '+49' },
  { iso: 'IT', name: 'Italy', code: '+39' }, { iso: 'ES', name: 'Spain', code: '+34' },
  { iso: 'NL', name: 'Netherlands', code: '+31' }, { iso: 'US', name: 'United States', code: '+1' },
  { iso: 'CA', name: 'Canada', code: '+1' }, { iso: 'AU', name: 'Australia', code: '+61' },
  { iso: 'ZA', name: 'South Africa', code: '+27' }, { iso: 'NG', name: 'Nigeria', code: '+234' },
];

const isMobile = () => window.matchMedia('(max-width: 991px)').matches;

// Staff can switch their own browser off from the admin analytics page
const trackingDisabled = () => {
  try {
    return window.localStorage.getItem('bv_ignore_analytics') === '1';
  } catch (error) {
    return false;
  }
};

const newDisplayId = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

// Fire-and-forget: sendBeacon finishes even while the page is navigating away
const trackEvent = (event, displayId) => {
  if (trackingDisabled()) return;
  const payload = JSON.stringify({
    campaign: CAMPAIGN,
    event,
    displayId,
    device: isMobile() ? 'mobile' : 'desktop',
  });
  const url = '/api/v1/campaign/popup-event';
  try {
    const sent = navigator.sendBeacon && navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
    if (!sent) {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
    }
  } catch (error) {
    // tracking must never affect the visitor
  }
};


function DiscountPopup({ onClose }) {
  const [showPopup, setShowPopup] = useState(false);
  const [step, setStep] = useState('offer'); // offer -> form -> success
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState(COUNTRIES[0]);
  const [dialOpen, setDialOpen] = useState(false);
  const dialRef = useRef(null);
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [promo, setPromo] = useState(null);
  const [copied, setCopied] = useState(false);
  // one id per time the popup is shown; the server ignores repeats of the same id
  const displayId = useRef(newDisplayId());
  const viewSent = useRef(false);

  useEffect(() => {
    if (isHidden()) return undefined;

    const timeout = setTimeout(() => {
      setShowPopup(true);
    }, 2000);

    return () => clearTimeout(timeout);
  }, []);

  // A view is counted only once the popup is really on screen
  useEffect(() => {
    if (showPopup && !viewSent.current) {
      viewSent.current = true;
      trackEvent('view', displayId.current);
    }
  }, [showPopup]);

  // Close the country list when clicking elsewhere
  useEffect(() => {
    if (!dialOpen) return undefined;
    const onDown = (event) => {
      if (dialRef.current && !dialRef.current.contains(event.target)) setDialOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [dialOpen]);

  const dismiss = () => {
    setShowPopup(false);
    if (typeof onClose === 'function') onClose();
  };

  // Close button / "No thanks": counted as a close
  const handleClose = () => {
    // a plain close leaves no cookie, so the popup comes back on the next reload
    if (step !== 'success') trackEvent('close', displayId.current);
    dismiss();
  };

  const handleStart = () => {
    trackEvent('click', displayId.current);
    setStep('form');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const cleanEmail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(cleanEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    const digits = phone.replace(/[\s()-]/g, '').replace(/^0+/, '');
    if (!/^[0-9]{6,14}$/.test(digits)) {
      setError('Please enter a valid mobile number.');
      return;
    }
    const cleanPhone = `${country.code} ${digits}`;
    if (!consent) {
      setError('Please tick the box to receive your code.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const response = await fetch('/api/v1/campaign/popup-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign: CAMPAIGN,
          email: cleanEmail,
          phone: cleanPhone,
          marketingConsent: consent,
          device: isMobile() ? 'mobile' : 'desktop',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.code) {
        throw new Error(data.message || 'Something went wrong. Please try again.');
      }
      setPromo({ code: data.code, expiresAt: data.expiresAt });
      try {
        window.localStorage.setItem(CODE_KEY, JSON.stringify({ code: data.code, expiresAt: data.expiresAt }));
      } catch (storageError) {
        // the code is also emailed, so storage is optional
      }
      hideFor(SIGNED_UP_DAYS);
      setStep('success');
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (copyError) {
      // the code is shown on screen, the visitor can select it by hand
    }
  };

  if (!showPopup) return null;

  return (
    <div className="wp-overlay" role="dialog" aria-modal="true" aria-label="Welcome offer">
      <div className="wp-card">
        <button className="wp-close" onClick={handleClose} aria-label="Close popup">&times;</button>

        <div className="wp-media">
          <img src={WelcomeImage} alt="BRAVE Athleisure athletes walking together" width="1080" height="1080" />
        </div>

        <div className="wp-panel">
          {step === 'offer' && (
            <div className="wp-body" key="offer">
              <p className="wp-eyebrow">Exclusive Welcome Offer</p>
              <h2 className="wp-title">UNLOCK<span>15% OFF</span></h2>
              <p className="wp-text">Get your 15% Code when you sign up. Code is valid only for new customers.</p>
              <button className="wp-primary" onClick={handleStart}>Sign Up to Reveal Code</button>
              <button className="wp-link" onClick={handleClose}>No thanks, I'll pay full price</button>
            </div>
          )}

          {step === 'form' && (
            <form className="wp-body" key="form" onSubmit={handleSubmit} noValidate>
              <button type="button" className="wp-back" onClick={() => setStep('offer')}>&larr; Back</button>
              <p className="wp-eyebrow">Step 2 of 2</p>
              <h2 className="wp-title wp-title-sm">Where should we send your code?</h2>
              <p className="wp-text">Enter your details to instantly reveal<br />your 15% discount code.</p>

              <label className="wp-label" htmlFor="welcome-email">Email Address *</label>
              <input id="welcome-email" className="wp-input" type="email" autoComplete="email" placeholder="athlete@brave.com" value={email} onChange={(e) => setEmail(e.target.value)} />

              <label className="wp-label" htmlFor="welcome-phone">Mobile Number *</label>
              <div className="wp-phone">
                <div className="wp-dial" ref={dialRef}>
                  <button type="button" className="wp-dial-btn" aria-haspopup="listbox" aria-expanded={dialOpen} aria-label={`Country code ${country.code}`} onClick={() => setDialOpen((open) => !open)}>
                    <span className="wp-dial-iso">{country.iso}</span>
                    <span>{country.code}</span>
                    <span className="wp-dial-caret" aria-hidden="true" />
                  </button>
                  {dialOpen && (
                    <ul className="wp-dial-list" role="listbox">
                      {COUNTRIES.map((item) => (
                        <li key={item.iso} role="option" aria-selected={item.iso === country.iso}>
                          <button type="button" className={item.iso === country.iso ? 'active' : ''} onClick={() => { setCountry(item); setDialOpen(false); }}>
                            <span className="wp-dial-name">{item.name}</span>
                            <span className="wp-dial-code">{item.code}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <input id="welcome-phone" className="wp-input" type="tel" inputMode="tel" autoComplete="tel-national" required placeholder="3600 0000" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>

              <label className="wp-consent">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span>I agree to receive marketing emails &amp; recurring automated SMS alerts from BRAVE Athleisure. Unsubscribe anytime.</span>
              </label>

              {error && <p className="wp-error" role="alert">{error}</p>}
              <button type="submit" className="wp-primary" disabled={submitting}>
                {submitting ? 'Please wait...' : 'Reveal My 15% Code'}
              </button>
            </form>
          )}

          {step === 'success' && promo && (
            <div className="wp-body" key="success">
              <p className="wp-eyebrow">YOU'RE IN</p>
              <h2 className="wp-title wp-title-sm">Your 15% off code</h2>
              <div className="wp-code">{promo.code}</div>
              <button className="wp-secondary" onClick={handleCopy}>{copied ? 'Copied!' : 'Copy code'}</button>
              <p className="wp-text">
                We also emailed it to you. One use per account. Not valid on promotional items.
                <br />
                <strong>Log in now and apply it at checkout!</strong>
              </p>
              <button className="wp-primary" onClick={handleClose}>Start Shopping</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DiscountPopup;
